import { useRef, useState } from 'react';

import {
  sortCodeToFormattedValue,
  stringToDigitsOnly,
} from '@shieldpay/utility-functions-ui/sort-code-to-formatted-value';

import { Box } from '../box/box';
import type { InputFieldProps } from '../input-field/input-field';
import { InputField } from '../input-field/input-field';

import { BankLogoFromSortcode } from './bank-logo-from-sortcode';

export type SortCodeInputBankLogoProps =
  | {
      showBankLogo: true;
      /**
       * required string to label the placeholder icon
       */
      bankPlaceholderIconAlt: string;
    }
  | {
      showBankLogo: false;
      bankPlaceholderIconAlt?: never;
    };

export type SortCodeInputProps = Omit<
  InputFieldProps,
  'type' | 'disabled' | 'className' | 'extraContainerStyles'
> &
  SortCodeInputBankLogoProps;

type SelectionDirection = 'forward' | 'backward' | 'none';

interface Selection {
  selectionStart: number;
  selectionEnd: number;
  selectionDirection: SelectionDirection;
}

/**
 * Function to compute adjusted selection range handling
 * the hyphenated display value.
 */
const adjustSelectionRange = (
  { selectionStart, selectionEnd, selectionDirection }: Selection,
  hyphenatedDisplayValue: string,
): Parameters<HTMLInputElement['setSelectionRange']> => {
  const adjustSelectionForHyphen =
    hyphenatedDisplayValue?.slice(selectionStart - 1, selectionEnd) === '-';

  return [
    adjustSelectionForHyphen ? selectionStart + 1 : selectionStart,
    adjustSelectionForHyphen ? selectionEnd + 1 : selectionEnd,
    selectionDirection,
  ];
};

export const SortCodeInput = ({
  defaultValue = '',
  label,
  helpText,
  'data-testid': testId,
  errors,
  'aria-invalid': invalid,
  'aria-describedby': errorMessageId,
  name,
  validating,
  showVisualErrorStyles = false,
  ...restProps
}: SortCodeInputProps) => {
  const {
    showBankLogo: _showBankLogo,
    bankPlaceholderIconAlt: _bankPlaceholderIconAlt,
    ...inputProps
  } = restProps;

  // inputRef used to get and then set adjusted selection values
  // to handle cursor placement.
  const inputRef = useRef<HTMLInputElement | null>(null);
  // nextSelectionRef used to store selection values to be applied when
  // controlled value is updated
  const nextSelectionArgumentsRef =
    useRef<Parameters<HTMLInputElement['setSelectionRange']>>(null);

  const handleChange = (value: string) => {
    if (!inputRef.current) return;

    // remove non-digits and constrains to 6 character length
    const sanitisedValue = stringToDigitsOnly(value).slice(0, 6);

    nextSelectionArgumentsRef.current = adjustSelectionRange(
      inputRef.current as Selection,
      sortCodeToFormattedValue(sanitisedValue),
    );

    inputRef.current.setSelectionRange(...nextSelectionArgumentsRef.current);

    setValue(sanitisedValue);

    inputRef.current.value = sortCodeToFormattedValue(sanitisedValue);

    inputRef?.current?.setSelectionRange(...nextSelectionArgumentsRef.current);
  };

  const [value, setValue] = useState<string>(defaultValue);

  return (
    <InputField
      {...inputProps}
      name={name}
      helpText={helpText}
      label={label}
      defaultValue={sortCodeToFormattedValue(defaultValue)}
      onChange={(e) => {
        handleChange(e.currentTarget.value);
      }}
      errors={errors}
      aria-invalid={invalid}
      aria-describedby={errorMessageId}
      validating={validating}
      showVisualErrorStyles={showVisualErrorStyles}
      data-testid={testId}
      ref={inputRef}
      type="tel"
    >
      {restProps.showBankLogo && (
        <Box
          alignContent={{ x: 'center', y: 'center' }}
          padding={{ right: 'baseNeg5' }}
        >
          <BankLogoFromSortcode
            sortCodePartial={value}
            bankPlaceholderIconAlt={restProps.bankPlaceholderIconAlt}
          />
        </Box>
      )}
    </InputField>
  );
};

export { BankNameFromSortcode } from './bank-name-from-sortcode';

export { BankLogoFromSortcode };
